import { useState, useEffect } from 'react';
import api from '../services/api';

// Premium Modules Index - Feature Orchestration
function ModulesIndex() {
    const [modules, setModules] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadModules();
    }, []);

    const loadModules = async () => {
        try {
            setLoading(true);
            const response = await api.get('/settings/modules');
            const modulesData = Array.isArray(response.data) ? response.data : (response.data?.modules || response.data?.data || []);
            setModules(modulesData);
        } catch (err) {
            console.error('Error loading modules:', err);
            setError('System synchronization failed for feature modules.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-black tracking-widest uppercase text-[10px]">Orchestrating Features...</div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header section */}
            <div className="mb-12">
                <h1 className="text-3xl sm:text-5xl font-black text-slate-900 tracking-tight">
                    System <span className="text-indigo-600">Capabilities</span>
                </h1>
                <p className="text-slate-500 mt-2 font-medium italic">Configure foundational modules and organizational features</p>
            </div>

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-6 rounded-3xl mb-10 shadow-sm">
                    <p className="text-sm font-black text-rose-800 uppercase tracking-wider">{error}</p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {modules.map((module) => (
                    <div key={module.id} className="group relative bg-white rounded-lg border border-slate-100 shadow-xl shadow-slate-200/50 p-8 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 hover:border-indigo-100 flex flex-col h-full bg-gradient-to-br from-white to-slate-50/30">
                        {/* Static Icon for now - in real app would come from module.icon */}
                        <div className="flex items-center justify-between mb-8">
                            <div className={`w-14 h-14 rounded-[1.25rem] flex items-center justify-center text-2xl shadow-inner transition-all group-hover:scale-110 group-hover:rotate-3 duration-500 ${module.enabled ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'
                                }`}>
                                {module.slug === 'inventory' ? '📦' :
                                    module.slug === 'sales' ? '💰' :
                                        module.slug === 'accounting' ? '📂' :
                                            module.slug === 'crm' ? '👥' : '🧩'}
                            </div>
                            <span className={`px-4 py-1.5 text-[9px] font-black uppercase tracking-[0.15em] rounded-full flex items-center gap-2 ${module.enabled
                                ? 'bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100'
                                : 'bg-slate-100 text-slate-400'
                                }`}>
                                {module.enabled ? (
                                    <>
                                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-pulse"></div>
                                        Active
                                    </>
                                ) : 'Standby'}
                            </span>
                        </div>

                        <div className="flex-1">
                            <h3 className="text-xl font-black text-slate-900 tracking-tight mb-3 group-hover:text-indigo-600 transition-colors">
                                {module.name}
                            </h3>
                            <p className="text-slate-500 text-sm font-bold opacity-80 leading-relaxed mb-6">
                                {module.description || 'Foundational capability enhancing organizational workflow and data integrity.'}
                            </p>
                        </div>

                        <div className="pt-6 border-t border-slate-100">
                            <button className={`w-full py-4 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 ${module.enabled
                                ? 'bg-white border border-slate-100 text-slate-400 cursor-not-allowed italic'
                                : 'bg-slate-900 text-white hover:bg-black hover:shadow-lg shadow-slate-200'
                                }`}>
                                {module.enabled ? 'Module Integrated' : 'Initialize Feature'}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default ModulesIndex;
